/**
 * Format a date as 'YYYY-MM-DD' using LOCAL date components.
 *
 * NOT equivalent to `new Date().toISOString().slice(0, 10)` — that converts
 * to UTC first, which silently shifts the date by 1 in non-UTC timezones
 * (e.g. IST evenings show "tomorrow" as "today" and vice versa).
 *
 * Use this whenever you need a date string for a date-only field
 * (slot dates, picker min/max, range filter bounds, etc.).
 *
 * @param offsetDays Days to add to today (negative for past).
 */
export function localIsoDate(offsetDays = 0): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offsetDays);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${String(year)}-${month}-${day}`;
}

/**
 * Render a 'YYYY-MM-DD' date as a short relative label suitable for badges
 * and pills. "Today" / "Tomorrow" for the immediate days, otherwise the
 * short weekday (Mon, Tue, Wed, ...). Past dates and dates beyond a week
 * fall back to "Mon, 12 Aug" so the label always says SOMETHING.
 *
 * Locale-fixed to en-US so weekday names stay short and consistent across
 * the wizard.
 */
export function formatRelativeDayLabel(dateISO: string): string {
  if (dateISO === localIsoDate(0)) return 'Today';
  if (dateISO === localIsoDate(1)) return 'Tomorrow';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateISO}T00:00:00`);
  const diffDays = Math.round((target.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));

  if (diffDays >= 2 && diffDays <= 6) {
    return target.toLocaleDateString('en-US', { weekday: 'short' });
  }
  return target.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' });
}
